import { Modal } from '@fluentui/react';
import { useCallback, useContext, useEffect, useState } from 'react';
import {
  SelectWrapper,
  SpeakerFiltersContainer,
  SpeakerModalContent,
  SpeakerModalHeader,
  SpeakersTableContainer,
  modalStyles,
} from './styles';
import { SpeakersTable } from './SpeakersTable';

import { callSpeechFunction } from '../../../../lib/Helpers';
import { TtsContext } from '../../../../contexts/TtsContext';
import Select, { Theme as ReactSelectTheme } from 'react-select';
import { useTheme } from 'styled-components';
import { Skeleton } from '../../../../../styles/components';
import { Block, useStore } from '../../../../store';
import { availableAudios } from '../../../../../assets/audios';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';

interface SpeakerModalInterface {
  isOpen: boolean;
  closeModal: () => void;
  blockId: string;
}

export interface Speaker {
  key: string;
  text: string;
  name: string;
  gender: string;
  locale: string;
  samplerate: string;
  type: string;
  status: string;
  styles: string[];
  sampleaudio: string;
}

export interface SpeakerFilters {
  language?: string;
  type?: string;
  gender?: string;
}

interface SelectOption {
  value: string;
  label: string;
}
interface FiltersOptions {
  languages?: SelectOption[];
  types?: SelectOption[];
  genders?: SelectOption[];
}

export const SpeakerModal = ({
  closeModal,
  isOpen,
  blockId,
}: SpeakerModalInterface) => {
  const appInsights = useAppInsightsContext();
  const appTheme = useTheme();

  const selectTheme = (theme: ReactSelectTheme) => {
    return {
      ...theme,
      borderRadius: 5,
      colors: {
        ...theme.colors,
        text: appTheme.black,
        neutral0: appTheme.neutralLighter,
        neutral5: appTheme.white,
        neutral10: appTheme.neutralLight,
        neutral20: appTheme.white,
        neutral30: appTheme.neutralPrimaryAlt,
        neutral40: appTheme.neutralSecondary,
        neutral50: appTheme.neutralTertiary,
        neutral60: appTheme.neutralTertiaryAlt,
        neutral70: appTheme.neutralQuaternary,
        neutral80: appTheme.neutralDark,
        neutral90: appTheme.black,
        primary50: appTheme.themeTertiary,
        primary75: appTheme.themeSecondary,
        primary25: appTheme.themeSecondary,
        primary: appTheme.themePrimary,
      },
    };
  };

  const selectStyles: any = {
    option: (provided: any, state: any) => ({
      ...provided,
      color: appTheme.black,
    }),
  };

  const { updateBlockControl, getBlock } = useStore();
  const [block, setBlock] = useState<Block>();
  const [audioUrl, setAudioUrl] = useState('');

  useEffect(() => {
    setBlock(getBlock(blockId));
  }, [blockId, getBlock]);

  const [preview, setPreview] = useState(false);
  const [speakers, setSpeakers] = useState<Speaker[]>();

  const [filters, setFilters] = useState<SpeakerFilters>({
    language: 'English (United States)',
  });
  const [options, setOptions] = useState<FiltersOptions>();

  const [searchTerm, setSearchTerm] = useState('');

  const { speechToken } = useContext(TtsContext);

  const handleSelect = useCallback(
    (selectedOption: Speaker) => {
      // console.log(selectedOption.text) // Speaker Short Name for Audio Samples

      if (
        block &&
        selectedOption.key &&
        block.controls.speaker.key !== selectedOption.key
      ) {
        updateBlockControl(blockId, 'speaker', selectedOption);
      }

      closeModal();
    },
    [block, blockId, closeModal]
  );

  const selectLanguage = (value: string | undefined) => {
    value && setFilters({ ...filters, language: value });
  };

  const selectGender = (value: string | undefined) => {
    value && setFilters({ ...filters, gender: value });
  };

  const selectType = (value: string | undefined) => {
    value && setFilters({ ...filters, type: value });
  };

  useEffect(() => {
    let isMounted = true;

    if (speechToken) {
      callSpeechFunction(
        'cognitiveservices/voices/list',
        speechToken.token,
        speechToken.region
      )
        .then((data) => {
          const fetchedSpeakers: Speaker[] = data.map((voice: any) => {
            const sampleaudio = availableAudios.includes(voice.ShortName)
              ? voice.ShortName + '.mp3'
              : '';

            return {
              key: voice.DisplayName,
              text: voice.ShortName,
              name: voice.Name,
              gender: voice.Gender,
              locale: voice.LocaleName,
              samplerate: voice.SampleRateHertz,
              type: voice.VoiceType,
              status: voice.Status,
              styles: voice.StyleList,
              sampleaudio,
            };
          });

          const languages: SelectOption[] = [
            {
              label: 'English (United States)',
              value: 'English (United States)',
            },
          ];
          const addedLaguages: any = {};
          fetchedSpeakers.forEach((item) => {
            if (!addedLaguages[item.locale]) {
              languages.push({ label: item.locale, value: item.locale });
              addedLaguages[item.locale] = true;
            }
          });

          const genders: SelectOption[] = [];
          const addedGenders: any = {};
          fetchedSpeakers.forEach((item) => {
            if (!addedGenders[item.gender]) {
              genders.push({ label: item.gender, value: item.gender });
              addedGenders[item.gender] = true;
            }
          });

          const types: SelectOption[] = [];
          const addedTypes: any = {};
          fetchedSpeakers.forEach((item) => {
            if (!addedTypes[item.type]) {
              types.push({ label: item.type, value: item.type });
              addedTypes[item.type] = true;
            }
          });

          if (isMounted) {
            setSpeakers(fetchedSpeakers);
            setOptions({ languages, genders, types });
          }
        })
        .catch((error) => {
          if (process.env.REACT_APP_APP_INSIGHTS !== '')
            appInsights.trackException({
              exception: error,
              properties: { component: 'TtsApp', location: 'onLoadVoices' },
            });
        });
    }

    return () => {
      isMounted = false;
    };
  }, [handleSelect, speechToken]);

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={closeModal}
      isBlocking={false}
      containerClassName={modalStyles.container}
    >
      <SpeakerModalContent>
        <SpeakerModalHeader>
          <h1>Set Speaker</h1>
          <h2>Select a voice to speak your sentence</h2>
        </SpeakerModalHeader>
        <SpeakerFiltersContainer>
          {options ? (
            <>
              <SelectWrapper>
                <span>Language</span>
                <Select
                  styles={selectStyles}
                  placeholder="Select an option"
                  options={options.languages}
                  onChange={(option) => selectLanguage(option?.value)}
                  theme={selectTheme}
                  defaultValue={{
                    label: filters?.language || 'English (United States)',
                    value: filters?.language || 'English (United States)',
                  }}
                />
              </SelectWrapper>
              <SelectWrapper>
                <span>Gender</span>
                <Select
                  placeholder="Select an option"
                  options={options.genders}
                  styles={selectStyles}
                  onChange={(option) => selectGender(option?.value)}
                  theme={selectTheme}
                  defaultValue={{
                    label: filters?.gender || 'Select an option',
                    value: filters?.gender || 'Select an option',
                  }}
                />
              </SelectWrapper>
              <SelectWrapper>
                <span>Types</span>
                <Select
                  styles={selectStyles}
                  placeholder="Select an option"
                  options={options.types}
                  onChange={(option) => selectType(option?.value)}
                  theme={selectTheme}
                  defaultValue={{
                    label: filters?.type || 'Select an option',
                    value: filters?.type || 'Select an option',
                  }}
                />
              </SelectWrapper>
              <SelectWrapper>
                <span>Search</span>
                <input
                  type="text"
                  name="filter"
                  placeholder="Type to search for a speaker..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </SelectWrapper>
            </>
          ) : (
            <>
              <Skeleton height={2.375} />
              <Skeleton height={2.375} />
              <Skeleton height={2.375} />
              <Skeleton height={2.375} />
            </>
          )}
        </SpeakerFiltersContainer>
        <SpeakersTableContainer data-is-scrollable="true">
          <SpeakersTable
            key={`speakers-table-${crypto.randomUUID()}`}
            speakers={speakers}
            filters={filters}
            searchTerm={searchTerm}
            preview={preview}
            onSelect={(speaker) => handleSelect(speaker)}
            onPlaySample={(speaker) => {
              import(
                `../../../../../assets/audios/${speaker.sampleaudio}`
              ).then((file) => {
                const audio = new Audio(file.default);
                audio.play();
              });
            }}
          />
        </SpeakersTableContainer>
      </SpeakerModalContent>
    </Modal>
  );
};
